/**
 * Thin wrapper over native fetch (F-ADR-004). Carries tenant and locale,
 * enforces a timeout, and turns failures into a discriminated union so a
 * component never has to guess what went wrong.
 */
import { i18n } from "@/shared/i18n";
import { beginLoading } from "./loading-bar";

export type HttpErrorKind =
	| "unauthorized"
	| "forbidden"
	| "notFound"
	| "validation"
	| "conflict"
	| "rateLimited"
	| "paymentRequired"
	| "server"
	| "network"
	| "timeout";

export class HttpError extends Error {
	constructor(
		readonly kind: HttpErrorKind,
		message: string,
		readonly status?: number,
		readonly issues?: ReadonlyArray<{ path: string; message: string }>,
		/** The whole error body, so a caller can read fields this class does not model. */
		readonly payload?: unknown,
	) {
		super(message);
		this.name = "HttpError";
	}
}

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * Where the access token comes from.
 *
 * Registered once at boot by the auth feature. Without this, every feature had to
 * import `authHeader` from `features/auth` — which the no-cross-feature rule
 * caught, and correctly: authentication is cross-cutting, not a sibling feature.
 *
 * PODE DEMORAR, e é por isso que devolve também uma promessa.
 *
 * No arranque a sessão é restaurada a partir do cookie de refresh, e as
 * consultas da moldura partem no mesmo tick — antes de existir token. O
 * resultado eram três 401 na consola a cada abertura do painel
 * (`/v3/structure/entities`, `/v3/workspace`, `/v3/platform/access`), com as
 * telas a repintar quando a repetição vinha boa. Quem sabe que o token está a
 * caminho é o dono da sessão; aqui só se espera por ele.
 */
type TokenProvider = () => string | null | Promise<string | null>;
let tokenProvider: TokenProvider = () => null;

export function setAuthTokenProvider(provider: TokenProvider): void {
	tokenProvider = provider;
}

/**
 * Where the PARTNER portal's token comes from — a second provider, on purpose.
 *
 * The partner portal is a different principal with a different credential: its
 * token is opaque and lives under its own Redis prefix, and the server cannot
 * resolve one in place of the other. Modelling that here as one provider with a
 * flag inside would rebuild, in the browser, exactly the mistake the back end
 * refused to inherit — two worlds separated by a field rather than by a wall.
 *
 * Nothing is registered until a partner screen boots, so an installation whose
 * user never opens the portal has a provider that answers `null` forever.
 */
let partnerTokenProvider: TokenProvider = () => null;

export function setPartnerTokenProvider(provider: TokenProvider): void {
	partnerTokenProvider = provider;
}

/**
 * The current token, for the one caller that cannot go through `request`.
 *
 * A streamed turn reads the response body itself, so it builds its own fetch —
 * and must still not reach into the auth feature to authenticate. Same reason
 * `setAuthTokenProvider` exists: authentication is cross-cutting, not a sibling
 * feature, and the architecture rule catches the shortcut.
 *
 * Assíncrona pela mesma razão que o provedor: no arranque o token pode ainda
 * estar a caminho, e quem monta o seu próprio `fetch` merece a mesma espera que
 * o `request` faz — não uma corrida perdida em silêncio.
 */
export async function getAuthToken(): Promise<string | null> {
	return tokenProvider();
}
const TIMEOUT_MS = 15_000;

function kindFor(status: number): HttpErrorKind {
	if (status === 401) return "unauthorized";
	if (status === 403) return "forbidden";
	if (status === 404) return "notFound";
	if (status === 409) return "conflict";
	if (status === 422 || status === 400) return "validation";
	if (status === 429) return "rateLimited";
	// 402 caía em `server`, e toda tela dizia "algo deu errado do nosso lado" — o
	// oposto do que aconteceu. Não houve falha nenhuma: uma porta está fechada, e
	// há duas que respondem assim (o teste vencido do `PlanGuard` e o módulo
	// desligado do `ModulesGuard`), ambas com um CÓDIGO no corpo que a tela lê.
	if (status === 402) return "paymentRequired";
	return "server";
}

export interface RequestOptions {
	readonly method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
	readonly body?: unknown;
	readonly tenantId?: string;
	readonly headers?: Record<string, string>;
	readonly signal?: AbortSignal;
	/**
	 * Overrides the default timeout.
	 *
	 * Needed because one request in this app is not like the others: an agent turn
	 * runs a tool loop — reading a site, writing a screen, testing an artifact — and
	 * routinely takes minutes. The 15s default is right for a list endpoint and would
	 * abort every builder turn.
	 */
	readonly timeoutMs?: number;
	/**
	 * WHICH credential this request carries.
	 *
	 * `'tenant'` is the default and the whole app keeps behaving exactly as it did.
	 * `'partner'` sends the portal's token and never the panel's; `'none'` sends
	 * neither, for a call made while signed out.
	 *
	 * `'cookie'` sends no Authorization header but DOES send cookies — it exists
	 * for exactly one caller, the session refresh, whose whole credential is the
	 * httpOnly `bx_refresh` cookie. It was `'none'` until 20/08, and `'none'`
	 * passou a significar `credentials: "omit"` no conserto do widget (16/08) —
	 * a partir daí toda renovação saía SEM o cookie, o servidor respondia 401
	 * NO_SESSION, e 401 é o único caso em que a sessão local se encerra: o
	 * painel derrubava a pessoa a cada quinze minutos, pontualmente.
	 *
	 * Explicit rather than inferred from the path: a request that guesses which
	 * identity it is speaking with is a request that will one day guess wrong, and
	 * the cost of guessing wrong here is a partner's token reaching a tenant route.
	 */
	readonly auth?: "tenant" | "partner" | "none" | "cookie";
}

/**
 * Reads the response body once and decides whether it is an answer or a
 * failure. Split out of `request` so neither function juggles both the
 * transport and the error taxonomy.
 *
 * 503 is deliberately not an error: the health endpoint answers 503 with a
 * valid report, and the caller is the one who decides what that means.
 */
async function interpret<T>(res: Response): Promise<T> {
	const text = await res.text();
	const payload = text ? (JSON.parse(text) as unknown) : undefined;

	if (res.ok || res.status === 503) return payload as T;

	const detail = payload as
		| { message?: string; issues?: ReadonlyArray<{ path: string; message: string }> }
		| undefined;
	throw new HttpError(
		kindFor(res.status),
		detail?.message ?? `request failed with ${res.status}`,
		res.status,
		detail?.issues,
		payload,
	);
}

function asHttpError(e: unknown, timeoutMs = TIMEOUT_MS): HttpError {
	if (e instanceof HttpError) return e;
	// A frase da casa, e não o "Failed to fetch" do navegador: toda tela que mostra
	// `e.message` mostra-a na língua do painel (QA 16). O cru fica no `cause`.
	if (e instanceof DOMException && e.name === "AbortError") {
		const cause = `no answer in ${timeoutMs}ms`;
		return Object.assign(new HttpError("timeout", i18n.global.t("errorKind.timeout")), { cause });
	}
	return Object.assign(new HttpError("network", i18n.global.t("errorKind.network")), { cause: e });
}

/**
 * QUEM SABE RENOVAR UMA SESSÃO EXPIRADA — registado uma vez, no arranque.
 *
 * O token de acesso vale quinze minutos e é renovado por um relógio no dono da
 * sessão. Um relógio não é uma garantia: a aba em segundo plano é estrangulada
 * pelo navegador, o portátil dorme, a renovação apanha um soluço de rede. Em
 * qualquer desses casos o pedido seguinte levava 401 e ficava por aí — a tela
 * mostrava "a sua sessão expirou" com um cookie de refresh válido por mais
 * vinte e nove dias no browser.
 *
 * Devolve `true` quando conseguiu renovar, e então o pedido é repetido UMA vez.
 * Aqui e não em `features/auth` pela mesma razão que o provedor de token:
 * autenticação é transversal, não uma feature irmã.
 */
type UnauthorizedRecovery = () => Promise<boolean>;
let recoverSession: UnauthorizedRecovery = async () => false;

export function setUnauthorizedRecovery(recovery: UnauthorizedRecovery): void {
	recoverSession = recovery;
}

/**
 * O `fetch` com Bearer de quem não pode passar por `request` (o multipart do
 * upload), com a mesma renovação: num 401 renova a sessão e repete UMA vez. Sem
 * isto, um anexo enviado depois de o token de 15 min vencer morria num 401.
 */
export async function fetchWithSession(url: string, init: RequestInit): Promise<Response> {
	const attempt = async () =>
		fetch(url, {
			...init,
			headers: {
				...(init.headers as Record<string, string>),
				Authorization: `Bearer ${(await getAuthToken()) ?? ""}`,
			},
		});
	const res = await attempt();
	if (res.status !== 401 || !(await recoverSession())) return res;
	return attempt();
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
	try {
		return await send<T>(path, opts);
	} catch (e) {
		// Só a credencial do tenant se renova aqui: o portal do parceiro tem outro
		// principal, e `'none'` diz explicitamente que não há sessão nenhuma para
		// renovar — incluindo a própria chamada de refresh, que não pode chamar-se
		// a si mesma.
		const renewable = (opts.auth ?? "tenant") === "tenant";
		if (!renewable || !(e instanceof HttpError) || e.kind !== "unauthorized") throw e;
		if (!(await recoverSession())) throw e;
		return await send<T>(path, opts);
	}
}

async function send<T>(path: string, opts: RequestOptions): Promise<T> {
	const controller = new AbortController();
	const timeoutMs = opts.timeoutMs ?? TIMEOUT_MS;
	const timer = setTimeout(() => controller.abort(), timeoutMs);
	opts.signal?.addEventListener("abort", () => controller.abort());
	// A barra do topo conta AQUI, e não em `request`: o 401 renovado é repetido
	// por `request` e isso são dois pedidos de verdade no fio — mais o refresh no
	// meio, que também passa por `send`.
	const endLoading = beginLoading("http");
	let failed = false;

	try {
		const res = await fetch(`${API_BASE}${path}`, {
			method: opts.method ?? "GET",
			headers: {
				...(opts.body ? { "Content-Type": "application/json" } : {}),
				...(opts.tenantId ? { "X-Tenant-Id": opts.tenantId } : {}),
				...(await authorization(opts.auth)),
				...opts.headers,
			},
			...(opts.body ? { body: JSON.stringify(opts.body) } : {}),
			/**
			 * OS COOKIES SEGUEM A IDENTIDADE, e não o contrário.
			 *
			 * Era `"include"` sempre, o que contradiz em silêncio a regra que
			 * `authorization` escreve logo abaixo: "uma tela que diz não precisar de
			 * credencial não pode mandar uma às escondidas". O cabeçalho não ia; o
			 * cookie ia.
			 *
			 * E não era cosmético. O chat público é servido dentro de um iframe com
			 * origem OPACA (o site do tenant vai com `CSP: sandbox`), e a superfície
			 * pública responde `Access-Control-Allow-Origin: *` — como deve, é
			 * pública. As duas coisas são incompatíveis: o navegador RECUSA um `*`
			 * quando o pedido vai com credenciais. O resultado era o widget a abrir,
			 * a pedir nome e telefone, e o botão a não fazer nada — a sessão nunca
			 * era criada porque o preflight morria em CORS. Na página `/c/:slug`
			 * nada disto aparecia: ali é a mesma origem e não há CORS nenhum.
			 *
			 * `"cookie"` existe porque esta linha cobrou caro uma vez: o refresh da
			 * sessão declarava `'none'` (não tem Bearer) e perdeu o cookie junto —
			 * a única credencial que ele tem. Ver o comentário em `auth`.
			 */
			credentials: opts.auth === "none" ? "omit" : "include",
			signal: controller.signal,
		});
		return await interpret<T>(res);
	} catch (e) {
		const error = asHttpError(e, timeoutMs);
		failed = paintsRed(error, opts.signal);
		throw error;
	} finally {
		clearTimeout(timer);
		endLoading(failed);
	}
}

/**
 * Que falha pinta a barra de vermelho: só a de VERDADE — o servidor que quebrou
 * (5xx), a rede que caiu, o pedido que não respondeu em 15 s.
 *
 * Um 4xx é uma RESPOSTA, e a tela que pediu é quem a lê e diz o que for preciso:
 * o 404 de uma referência apagada vira "(registro excluído)", o 409 vira a
 * pergunta, o 400 vira a mensagem do campo. Pintar a barra por eles (dono,
 * 18/10/2026) era um alarme sobre o funcionamento normal do sistema. O 401
 * renova-se sozinho e o 402 é uma porta de plano — nenhum dos dois é falha.
 *
 * Abortado por QUEM PEDIU (`opts.signal`) também não pinta: houve desistência,
 * não erro (`asHttpError` chama-o `timeout`; é o sinal do chamador que distingue).
 */
function paintsRed(error: HttpError, callerSignal: AbortSignal | undefined): boolean {
	if (callerSignal?.aborted) return false;
	// 503 BUSY é o servidor a pedir outra tentativa sem ter quebrado nada; a tela diz a frase.
	if ((error.payload as { error?: unknown } | undefined)?.error === "BUSY") return false;
	return error.kind === "server" || error.kind === "network" || error.kind === "timeout";
}

/**
 * One place decides which token — if any — leaves the browser.
 *
 * `'none'` returns nothing at all rather than falling back to the tenant token:
 * a screen that says it needs no credential must not quietly send one.
 */
async function authorization(
	auth: RequestOptions["auth"] = "tenant",
): Promise<Record<string, string>> {
	// `"cookie"` também não leva cabeçalho — e não pode esperar pelo provedor de
	// token: o refresh é justamente quem o alimenta, e esperaria por si mesmo.
	if (auth === "none" || auth === "cookie") return {};
	const token = await (auth === "partner" ? partnerTokenProvider() : tokenProvider());
	return token ? { Authorization: `Bearer ${token}` } : {};
}
